import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { itineraryApi } from '../services/api';
import { toast } from 'sonner';
import {
  BookOpen, MapPin, Calendar, ChevronRight, Loader2, Trash2, Share2,
  ArrowLeft, Camera, StickyNote, Clock, X, Copy, MessageCircle,
  Twitter, Facebook, Link2, Mail, FileText, Download
} from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { downloadTimelinePdf } from '../utils/downloadTimelinePdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

const StoryCard = ({ story, onRefresh }) => {
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const handleShare = async (e) => {
    e.stopPropagation();
    setSharing(true);
    try {
      const res = await itineraryApi.createTimelineShare(story.itinerary_id);
      if (res.data.success) {
        const url = `${window.location.origin}/shared/timeline/${res.data.share_id}`;
        setShareUrl(url);
        setShowShareDialog(true);
      }
    } catch { toast.error('Failed to share story'); }
    finally { setSharing(false); }
  };

  const shareText = `Check out my trip to ${story.destination_city}! Planned with Triple A Travel Solutions.`;
  const shareWhatsApp = () => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`, '_blank');
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
  const shareEmail = () => {
    const subject = encodeURIComponent(`My Trip Story - ${story.destination_city}`);
    const body = encodeURIComponent(`${shareText}\n\nView my trip story: ${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };
  const copyShareLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied!'); }
    catch { toast.error('Failed to copy'); }
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await itineraryApi.getTimeline(story.itinerary_id);
      if (res.data) {
        downloadTimelinePdf(res.data);
        toast.success('Generating PDF...');
      }
    } catch { toast.error('Failed to download PDF'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await itineraryApi.deleteTimeline(story.itinerary_id);
      toast.success('Story deleted');
      onRefresh();
    } catch { toast.error('Failed to delete story'); }
    finally { setDeleting(false); setDeleteOpen(false); }
  };

  return (
    <motion.div variants={fadeUp}>
      <Card
        className="bg-white/95 backdrop-blur-xl border border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
        onClick={() => navigate(`/itinerary/${story.itinerary_id}/timeline`)}
        data-testid={`story-card-${story.itinerary_id}`}
      >
        {/* Preview grid */}
        <div className="grid grid-cols-2 gap-0.5 h-44 overflow-hidden">
          {story.preview_paths.length > 0 ? (
            story.preview_paths.slice(0, 4).map((path, i) => (
              <div key={`preview-${path}`} className={`relative overflow-hidden ${story.preview_paths.length === 1 ? 'col-span-2 row-span-2' : story.preview_paths.length === 2 ? 'row-span-2' : story.preview_paths.length === 3 && i === 0 ? 'row-span-2' : ''}`}>
                <img
                  src={`${API_URL}/api/journal/file/${path}`}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-ocean/10', 'flex', 'items-center', 'justify-center'); }}
                />
              </div>
            ))
          ) : (
            <div className="col-span-2 row-span-2 bg-ocean/5 flex items-center justify-center">
              <Clock className="w-10 h-10 text-ocean/20" />
            </div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-serif text-lg text-ocean font-bold truncate">
                {story.destination_city}
              </h3>
              {story.destination_country && (
                <p className="text-charcoal/50 text-xs flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {story.destination_country}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 mt-1">
              <button onClick={handleShare} disabled={sharing}
                className="w-8 h-8 rounded-full bg-ocean/10 hover:bg-ocean/20 text-ocean flex items-center justify-center transition-colors"
                data-testid={`share-story-${story.itinerary_id}`}>
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              </button>
              <button onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
                className="w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 flex items-center justify-center transition-colors"
                data-testid={`delete-story-${story.itinerary_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
              <ChevronRight className="w-5 h-5 text-ocean/30 group-hover:text-ocean group-hover:translate-x-0.5 transition-all" />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-3 text-xs text-charcoal/50">
            {story.start_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {story.start_date}
              </span>
            )}
            <span className="flex items-center gap-1 font-medium text-ocean">
              <Camera className="w-3 h-3" /> {story.photo_count} photo{story.photo_count !== 1 ? 's' : ''}
            </span>
            {story.note_count > 0 && (
              <span className="flex items-center gap-1 font-medium text-amber-600">
                <StickyNote className="w-3 h-3" /> {story.note_count} note{story.note_count !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent onClick={e => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Story?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all story notes and shares for "{story.destination_city}". Photos and the trip itself are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Dialog */}
      <AnimatePresence>
        {showShareDialog && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowShareDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
              data-testid={`share-dialog-story-${story.itinerary_id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-ocean font-semibold text-lg flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-gold" /> Share Trip Story
                </h3>
                <button onClick={() => setShowShareDialog(false)} className="text-charcoal/40 hover:text-charcoal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-charcoal/60 text-sm mb-2">Share your trip story for <strong>{story.destination_city}</strong>:</p>

              {/* PDF file display */}
              <div className="bg-ocean/5 border border-ocean/15 rounded-xl p-4 mb-4 flex items-center gap-3" data-testid={`story-pdf-display-${story.itinerary_id}`}>
                <div className="w-10 h-10 bg-ocean/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-ocean" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-ocean truncate">{story.destination_city} - Trip Story.pdf</p>
                  <p className="text-xs text-charcoal/50">PDF Trip Timeline</p>
                </div>
                <Button onClick={handleDownloadPdf} size="sm" className="bg-gold hover:bg-gold/90 text-ocean rounded-full text-xs px-4 h-8 font-semibold shadow" data-testid={`story-download-pdf-${story.itinerary_id}`}>
                  <Download className="w-3.5 h-3.5 mr-1" /> Download
                </Button>
              </div>

              <p className="text-charcoal/60 text-sm mb-2">Share link:</p>
              <div className="flex gap-2 mb-4">
                <input type="text" readOnly value={shareUrl}
                  className="flex-1 bg-gray-100 rounded-lg px-3 py-2 text-sm text-charcoal border border-gray-200"
                  data-testid="story-share-url-input" />
                <Button onClick={copyShareLink} className="bg-ocean hover:bg-ocean-light text-white rounded-lg" data-testid="story-copy-link-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={shareWhatsApp} variant="outline" className="rounded-xl h-11 border-green-300 text-green-700 hover:bg-green-50" data-testid="story-whatsapp-btn">
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                </Button>
                <Button onClick={shareTwitter} variant="outline" className="rounded-xl h-11 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="story-twitter-btn">
                  <Twitter className="w-4 h-4 mr-2" /> Twitter / X
                </Button>
                <Button onClick={shareFacebook} variant="outline" className="rounded-xl h-11 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="story-facebook-btn">
                  <Facebook className="w-4 h-4 mr-2" /> Facebook
                </Button>
                <Button onClick={copyShareLink} variant="outline" className="rounded-xl h-11 border-ocean/20 text-ocean hover:bg-ocean/5" data-testid="story-copylink-btn">
                  <Link2 className="w-4 h-4 mr-2" /> Copy Link
                </Button>
                <Button onClick={shareEmail} variant="outline" className="rounded-xl h-11 border-amber-300 text-amber-700 hover:bg-amber-50 col-span-2" data-testid="story-email-btn">
                  <Mail className="w-4 h-4 mr-2" /> Email to Friends
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function MyStoriesPage() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchStories = useCallback(async () => {
    try {
      const res = await itineraryApi.listStories();
      if (res.data.success) setStories(res.data.stories);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
      <Header />

      <main className="pt-24 pb-20 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-stories">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>

          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl text-white font-bold flex items-center gap-3" data-testid="stories-page-title">
              <Clock className="w-8 h-8 text-gold" /> Trip Stories
            </h1>
            <p className="text-white/60 text-sm mt-2">Your travel stories and timelines</p>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-gold" />
            </div>
          ) : stories.length === 0 ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-white/95 backdrop-blur-xl border border-white/30 shadow-lg">
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 bg-ocean/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-ocean/40" />
                  </div>
                  <h3 className="font-serif text-xl text-ocean font-bold mb-2">No Stories Yet</h3>
                  <p className="text-charcoal/50 text-sm mb-6 max-w-md mx-auto">
                    Create a trip and visit the Trip Story tab to start building your travel timeline.
                  </p>
                  <Button onClick={() => navigate('/dashboard')}
                    className="bg-gold hover:bg-gold-dark text-ocean font-semibold rounded-lg px-6"
                    data-testid="go-to-trips-stories">Go to My Trips</Button>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <motion.div initial="hidden" animate="show" variants={stagger}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {stories.map(s => (
                <StoryCard key={s.itinerary_id} story={s} onRefresh={fetchStories} />
              ))}
            </motion.div>
          )}

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-stories-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
